import Constant from '@sailor/ksi-constants';
import { getCurrentPosition } from '@mtfe/mars-webloc/es';
import {
  safeParse, isProd, getLoginAccountId,
} from '@m2h/common/utils/common';
import {
  getRegion, getCityId, setRegion, setCityId,
} from '@m2h/common/utils';
import { REGION_MAP, SUPPORT_REGIONS } from '@sailor/compass';
import { MCity, MPhone } from '@sailor/i18n-web';
import { $post, $get } from '@/src/pages/address/selector_page/request';
import { pureRequest } from '../request/index.h5';
import { sessionKeys, isWebAlert, localKeys } from '../constants';
import { DefaultLocation } from '../constants/locationConf';
import { apiGetRegionConfigs } from '../constants/apiUrl';
import { LocatinStatus, LocatinType } from '@/src/sub_packages/homepage/address_bar/constant';

const REGION_CHANGE_STATUS = {
  failed: 1,
  success: 2,
};

const LOCATION_CACHE_NORMALIZED_FLAG = '__m2h_location_cache_normalized__';
const SUPPORT_REGION_SET = new Set(SUPPORT_REGIONS);

let locationSelectionRevision = 0;
let locationCacheNormalizedInCurrentRealm = false;

const isNonEmptyValue = (value) => value !== undefined && value !== null && String(value).trim() !== '';

const normalizeCoordinate = (value, min: number, max: number) => {
  if (!isNonEmptyValue(value)) return null;
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue) || numericValue < min || numericValue > max) return null;
  return typeof value === 'string' ? value.trim() : String(value);
};

export const normalizeLocationCoordinates = (latitude, longitude) => {
  const normalizedLatitude = normalizeCoordinate(latitude, -90, 90);
  const normalizedLongitude = normalizeCoordinate(longitude, -180, 180);
  if (normalizedLatitude === null || normalizedLongitude === null) return null;
  return {
    latitude: normalizedLatitude,
    longitude: normalizedLongitude,
  };
};

const getHostMach = () => {
  try {
    if (window.self !== window.top && window.top?.Mach?.requireModule) {
      return window.top.Mach;
    }
  } catch (e) {
    // 跨域 iframe 无法读取 top 时退回当前 Mach。
  }
  return Mach;
};

const emitHostEvent = (eventName: string, data: any = {}) => {
  try {
    getHostMach()?.requireModule?.('EventCenter')?.emit?.(eventName, data);
  } catch (e) {
    // 宿主事件异常不能打断定位状态提交或 Region 失败兜底。
    console.log('emitHostEvent error', e);
  }
};

const emitRegionChangeResult = (success: boolean) => {
  emitHostEvent('com.keeta.changed.region', {
    coreStatus: success ? REGION_CHANGE_STATUS.success : REGION_CHANGE_STATUS.failed,
  });
};

const emitHomeCoreComplete = () => {
  emitHostEvent('homecore_complete_event', {});
};

// 首页定位状态
// 0 : 初始化：未启动首页定位 地址栏展示'定位中' /// i18n key :  shouye_address_locating
// 1 : 定位中：真实定位中 or 逆地理中 地址栏展示'定位中'
// 2 : 定位完成，但是逆地理失败：地址栏展示'未选择地址' /// i18n key : address_mainpage_addressnotchosen
// 3 : 使用默认旺角地址
// 4 : 匹配到真实定位300m内的收货地址
// 5 : 真实定位poi : 逆地理成功
// 6 : 在地图中选择的地点
// 7 ：手动选择的用户收货地址
// 8 : 定位失败后使用最近一次真实定位缓存
// 9 ： 定位失败后使用最近一次地图选点
// 10 ： 定位失败后使用最近一次收货地址记录
// 11 ： 切换用户后收货地址已过期，此时只能取到经纬度和地址名称，其他信息已被清空

// 各地区默认定位地址
export const defaultLocation = () => {
  const currRegion = getRegion() || REGION_MAP.HK;
  return DefaultLocation[currRegion] || DefaultLocation.HK;
};

/**
 * 将当前定位快照整体覆盖写入 localStorage。
 *
 * - 存储键为 `localKeys.LOCATION_INFO`，实际值是 `m2h_location_info`；
 * - 通过 JSON 序列化保存，供跨页面、跨 bundle、跨会话恢复最近一次定位状态；
 * - 本函数只负责持久化，不会合并旧缓存、广播定位变化或校验坐标业务语义；
 * - 每次写入都会以 Compass 当前值覆盖 `region`、`cityId`，避免各入口再次产生缺字段或跨区快照；
 * - 缓存中的 Region 仍不是当前业务 Region 的权威来源，读取业务状态应使用
 *   `getRegion()` / `getCityId()`；
 * - JSON 序列化或 Storage 写入异常会继续抛给调用方，以便切 Region 等事务执行回滚。
 *
 * 需要在写入后广播定位变化时，应调用 `setLocationInfo()`。
 *
 * @param data 需要持久化的完整定位快照
 * @returns 已补齐当前 Region、CityId 并实际写入的快照
 */
export function setStoredCurrLocation(data) {
  const runtimeLocation = {
    ...(data || {}),
    region: getRegion(),
    cityId: getCityId(),
  };
  localStorage?.setItem(localKeys.LOCATION_INFO, JSON.stringify(runtimeLocation));
  return runtimeLocation;
}

// 获取存储的定位信息
export function getStoredCurrLocation() {
  const locationJson = localStorage?.getItem(localKeys.LOCATION_INFO)
    || sessionStorage?.getItem(sessionKeys.LOCATION_INFO) || '';
  return safeParse(locationJson, {});
}

export const getStoredSelectedUserAddress = () => {
  const addressJson = sessionStorage?.getItem(sessionKeys.HOME_SELSECTED_ADDRESS) || '';
  return safeParse(addressJson, {});
};

export const setStoredSelectedUserAddress = (address) => {
  sessionStorage?.setItem(sessionKeys.HOME_SELSECTED_ADDRESS, JSON.stringify(address || {}));
  window.Mach?.pageInitConfig?.({ machConfig: { homeSelectedUserAddress: address || null } });
};

export const clearStoredSelectedUserAddress = () => {
  setStoredSelectedUserAddress(null);
};

const hasObjectData = (value) => value && typeof value === 'object' && Object.keys(value).length > 0;

const normalizeLocationRegion = (value) => (
  typeof value === 'string' ? value.trim().toUpperCase() : ''
);

const parseTimeZoneOffset = (timezone: string) => {
  if (timezone === 'GMT') return 0;
  const match = /^GMT([+-])(\d{1,2})(?::(\d{2}))?$/.exec(timezone);
  if (!match) return undefined;
  const minutes = Number(match[2]) * 60 + Number(match[3] || 0);
  return match[1] === '-' ? -minutes : minutes;
};

const getTimeZoneDisplayName = (timeZone: string, timeZoneName: string) => {
  try {
    const formatter = new globalThis.Intl.DateTimeFormat('en-US', {
      timeZone,
      timeZoneName,
    } as any);
    return formatter.formatToParts(new Date())
      .find((part) => part.type === 'timeZoneName')?.value;
  } catch (e) {
    return undefined;
  }
};

const getRegionLocationMetadata = (region: string, cityId: number) => {
  const metadata: {
    interCode?: string;
    timezone?: string;
    timezoneOffset?: number;
    timezoneName?: string;
  } = {};
  try {
    const interCode = MPhone.getCountryCallingCodeByRegion(
      region as Parameters<typeof MPhone.getCountryCallingCodeByRegion>[0]
    );
    if (typeof interCode === 'string' && /^\d{1,3}$/.test(interCode)) {
      metadata.interCode = interCode;
    }
  } catch (e) {
    // 国际区号缺失时保持目标 Region 快照为空，不能回退旧 Region 的值。
  }

  try {
    const timeZone = MCity.getTimeZoneByCityId(cityId);
    if (typeof timeZone !== 'string' || !timeZone.trim()) return metadata;
    const timezone = getTimeZoneDisplayName(timeZone, 'longOffset');
    const timezoneName = getTimeZoneDisplayName(timeZone, 'long');
    if (timezone) {
      metadata.timezone = timezone;
      const timezoneOffset = parseTimeZoneOffset(timezone);
      if (timezoneOffset !== undefined) metadata.timezoneOffset = timezoneOffset;
    }
    if (timezoneName) metadata.timezoneName = timezoneName;
  } catch (e) {
    // 时区数据缺失时保持目标 Region 快照为空，不能回退旧 Region 的值。
  }
  return metadata;
};

const clearInitialRegionCityMismatch = () => {
  try {
    window.__initRegionCityMismatch = false;
  } catch (e) {
    // 非标准宿主不允许写 window 扩展字段时不影响已完成的状态校正。
  }
};

/**
 * 启动时以 Region 为权威校正 CityId 和历史定位缓存。
 *
 * 这里只迁移持久状态，不广播用户切区事件。显式切区仍由
 * changeLocationBySelectLocation() 保持原有事务、回滚与通知语义。
 */
export const reconcileRuntimeLocationState = () => {
  const region = normalizeLocationRegion(getRegion());
  const configuredLocation = DefaultLocation[region];
  if (!hasObjectData(configuredLocation)) {
    // MG、OM 当前没有权威默认定位配置，不能伪造 HK 定位并标记成对应 Region。
    return getStoredCurrLocation();
  }

  const expectedCityId = Number(configuredLocation.cityId);
  const currentCityId = Number(getCityId());
  let initialPairMismatch = currentCityId !== expectedCityId;
  try {
    initialPairMismatch = initialPairMismatch || window.__initRegionCityMismatch === true;
  } catch (e) {
    // 非标准宿主读取 window 扩展字段失败时，以当前 pair 判断为准。
  }

  if (currentCityId !== expectedCityId) {
    setCityId(expectedCityId);
  }
  if (Number(getCityId()) !== expectedCityId) {
    // CityId 未成功提交时不继续写定位，保留标记供下一次启动重试。
    return getStoredCurrLocation();
  }
  try {
    window.__initCityId = expectedCityId;
  } catch (e) {
    // 运行时兜底字段写入失败不影响 Compass 已提交状态。
  }

  const storedLocation = getStoredCurrLocation();
  if (!hasObjectData(storedLocation)) {
    // 空缓存仍交给 setCurrentLocation() 获取真实定位，不能提前写默认坐标阻断定位。
    clearInitialRegionCityMismatch();
    return storedLocation;
  }

  const storedRegion = normalizeLocationRegion(storedLocation.region);
  const storedCityId = Number(storedLocation.cityId);
  const shouldResetLocation = initialPairMismatch
    || storedRegion !== region
    || (Number.isSafeInteger(storedCityId) && storedCityId !== expectedCityId);
  const shouldStampRuntimeState = storedLocation.region !== region
    || storedCityId !== expectedCityId;

  try {
    let reconciledLocation = storedLocation;
    if (shouldResetLocation) {
      reconciledLocation = setStoredCurrLocation({
        ...configuredLocation,
        region,
        cityId: expectedCityId,
      });
    } else if (shouldStampRuntimeState) {
      reconciledLocation = setStoredCurrLocation({
        ...storedLocation,
        region,
        cityId: expectedCityId,
      });
    }
    clearInitialRegionCityMismatch();
    return reconciledLocation;
  } catch (e) {
    console.log('reconcileRuntimeLocationState error', e);
    return storedLocation;
  }
};

const isLocationCacheNormalizedForRuntime = () => {
  try {
    const runtimeWindow: any = window.top || window;
    if (runtimeWindow[LOCATION_CACHE_NORMALIZED_FLAG]) return true;
    runtimeWindow[LOCATION_CACHE_NORMALIZED_FLAG] = true;
    return false;
  } catch (e) {
    // 跨域 WindowProxy 不允许读写自定义属性时，退回当前 JS realm 标记。
    if (locationCacheNormalizedInCurrentRealm) return true;
    locationCacheNormalizedInCurrentRealm = true;
    return false;
  }
};

export const normalizeStoredLocationForRuntime = () => {
  const storedLocation = getStoredCurrLocation();
  if (isLocationCacheNormalizedForRuntime()) return storedLocation;

  const locateStatus = Number(storedLocation?.locateStatus);
  const type = Number(storedLocation?.type);
  // 空缓存或残缺缓存会得到 NaN；必须提前返回，否则 NaN !== NaN 会被误判为状态发生变化，
  // 最终返回 { locateStatus: NaN, type: NaN }，并在 JSON 序列化后把两个字段污染为 null。
  if (!Number.isFinite(locateStatus) || !Number.isFinite(type)) return storedLocation;

  let normalizedStatus = locateStatus;
  let normalizedType = type;

  if (locateStatus === LocatinStatus.actualLocation && type === LocatinType.actualPoi) {
    normalizedStatus = LocatinStatus.cachedActualLocation;
  } else if (
    (locateStatus === LocatinStatus.selectedLocation && type === LocatinType.selectedLocation)
    || (locateStatus === LocatinStatus.selectedUserAddress && type === LocatinType.selectedLocation)
  ) {
    // 兼容历史错误组合 7/2：旧 Web 将地图选点写成了 selectedUserAddress。
    normalizedStatus = LocatinStatus.cachedSelectedLocation;
    normalizedType = LocatinType.selectedLocation;
  } else if (
    (locateStatus === LocatinStatus.selectedUserAddress && type === LocatinType.userAddress)
    || (locateStatus === LocatinStatus.cachedSelectedUserAddress && type === LocatinType.selectedLocation)
  ) {
    // 兼容历史错误组合 10/2：旧 Web 将收货地址写成了地图类型。
    normalizedStatus = LocatinStatus.cachedSelectedUserAddress;
    normalizedType = LocatinType.userAddress;
  }

  const normalizedLocation = normalizedStatus !== locateStatus || normalizedType !== type
    ? {
      ...storedLocation,
      locateStatus: normalizedStatus,
      type: normalizedType,
    }
    : storedLocation;

  if (normalizedLocation !== storedLocation) {
    setStoredCurrLocation(normalizedLocation);
  }

  if (
    normalizedType === LocatinType.userAddress
    && hasObjectData(normalizedLocation?.detail)
    && !hasObjectData(getStoredSelectedUserAddress())
  ) {
    setStoredSelectedUserAddress(normalizedLocation.detail);
  }

  return normalizedLocation;
};

// 存储上次定位信息
export const storeLastSelected = (address) => {
  const runtimeLocation = {
    ...(address || {}),
    region: getRegion(),
    cityId: getCityId(),
  };
  localStorage?.setItem(localKeys.ADDRESS_LAST_SELSECTED, JSON.stringify(runtimeLocation));
  return runtimeLocation;
};

// 获取存储的上次定位信息, sessionStorage 过渡期兜底，后续去除
export const getStoreLastSelected = () => {
  const locationJson = localStorage?.getItem(localKeys.ADDRESS_LAST_SELSECTED)
    || sessionStorage?.getItem(sessionKeys.ADDRESS_LAST_SELSECTED) || '';
  const storedLocation = safeParse(locationJson, {});
  if (!hasObjectData(storedLocation)) return storedLocation;
  const region = normalizeLocationRegion(getRegion());
  const cityId = Number(getCityId());
  if (normalizeLocationRegion(storedLocation.region) !== region
    || Number(storedLocation.cityId) !== cityId) return {};
  return storedLocation;
};

// 存储region信息
export const setRegionConfigs = (address) => {
  localStorage.setItem(localKeys.REGION_CONFIGS, JSON.stringify(address));
};

// 获取存储的region信息
export const getRegionConfigs = () => {
  const jsonstr = localStorage.getItem(localKeys.REGION_CONFIGS)
    || sessionStorage.getItem(sessionKeys.REGION_CONFIGS) || '';
  return safeParse(jsonstr, {});
};

// nearbyAddress: 'v1/lbs/address/nearby_address', // 逆地址
export const getNearbyAddress = async (params: { longitude: string; latitude: string; }) => {
  try {
    const { longitude, latitude } = params || {};
    const [err, addressInfo] = await $post({
      url: 'v1/address/user/getNearbyShop',
      data: {
        userLongitude: longitude,
        userLatitude: latitude,
      },
    });

    // const [err, addressInfo] = await $get({
    //   url: 'v1/lbs/address/nearby_address',
    //   data: {
    //     keyword: '',
    //     bundleVersion: Mach.env.bundleVersion,
    //     scene: 1, // 开启过滤重复的解构化数据
    //     pageToken: '',
    //     location: latitude && longitude ? `${longitude},${latitude}` : '',
    //   },
    // });

    if (err || !addressInfo) {
      return {
        longitude,
        latitude,
        inServiceArea: true,
      };
    }
    const {
      address, hasShop, poiInfo, poiName
    } = addressInfo || {};
    // if (!hasShop) return {};
    // const { location } = safeParse(poiInfo) || {};
    // const locationArr = location?.split(',')?.map(Number) || [];
    return {
      // longitude: locationArr[0],
      // latitude: locationArr[1],
      longitude,
      latitude,
      poiName,
      addressLocation: address,
      inServiceArea: true,
    };
  } catch (e) {
    console.log(e);
    return null;
  }
};

// 初试话region配置信息
export const initRegionConfigs = async () => {
  try {
    const regionConfigs = getRegionConfigs();
    if (regionConfigs.supportRegions) return;
    const res = await pureRequest({
      url: apiGetRegionConfigs,
      method: 'POST',
      headers: {
        clientType: 'c_ios',
      },
      withCredentials: false, // 不携带cookie
    });
    if (res.code === 0) {
      setRegionConfigs(res.data);
    }
  } catch (e) {
    console.log('initRegionConfigs error', e);
  }
};

// 获取定位，当前存储定位，+默认定位兜底
export const getLocation = (location?) => {
  return {
    ...defaultLocation(),
    ...getStoredCurrLocation(),
    ...location,
    region: getRegion(),
    cityId: getCityId(),
  };
};

// 通过定位包@mtfe/mars-webloc/es，获取定位信息
export const getPosition: () => Promise<{ actualLatitude: string; actualLongitude: string; }> = () =>
  new Promise((resolve, reject) => {
    // 不能用于逆地址获取，逆地理服务相关后端服务部署在境内，境外使用有合规风险
    getCurrentPosition(
      {
        // @ts-ignore
        project: window.__process_env_appkey || 'com.sankuai.sailorfe.c.h5', // 调用方可根据自己业务名称填写(例如package.json的name)
        // @ts-ignore
        prejectVersion: process_env_formatTimestamp || 'version',
        uuid: Mach.env.uuid ?? '', // 配置用户uuid，方便根据uuid查询日志，
        userid: getLoginAccountId(), // 配置用户userid，方便根据userid查询日志
        // key: '6b45827838e643723ff7803d4d15f46f',
        key: '6c9281690b5c7c9b5b7dbd8a71cb2187',
        city: false,
      },
      (err, result) => {
        if (err) return reject(err);
        resolve({
          actualLatitude: result?.geo?.latitude || '',
          actualLongitude: result?.geo?.longitude || '',
        });
      }
    );
  });

// 获取实际定位对应的坐标， 需要更新actual
export const getOncePosition = async () => {
  const { actualLatitude, actualLongitude } = await getPosition();
  const {
    longitude, latitude, poiName, addressLocation, inServiceArea
  } = (await getNearbyAddress({ latitude: actualLatitude, longitude: actualLongitude })) || {};
  return {
    actualLatitude,
    actualLongitude,
    longitude,
    latitude,
    poiName,
    name: poiName,
    addressLocation,
    inServiceArea,
    detail: {},
    locateStatus: LocatinStatus.actualLocation,
    type: LocatinType.actualPoi,
  };
};

// 存储定位信息，并广播通知
const notifyLocationChanged = (mach, data) => {
  let eventCenter;
  try {
    eventCenter = mach?.requireModule?.('EventCenter');
  } catch (e) {
    // 单个通知通道不可用时不影响已提交的定位状态。
  }
  try {
    eventCenter?.emit?.('com.keeta.home.locate.changed', data);
  } catch (e) {
    // 监听器异常不能阻断后续通知。
  }
  try {
    eventCenter?.emit?.(Constant.c.ACTION.KSI_REFRESH_HOMEPAGE_LOCATION_COMPLETE, data);
  } catch (e) {
    // 监听器异常不能回滚已经落库的定位状态。
  }
  try {
    mach?.sendEvent?.('refreshHomePage');
  } catch (e) {
    // Mach 页面事件失败与定位持久状态解耦。
  }
};

export const setLocationInfo = (data: any) => {
  // 只有持久写入失败才向调用方抛错；广播本身不可回滚，需逐个隔离异常。
  const runtimeLocation = setStoredCurrLocation(data);
  notifyLocationChanged(Mach, runtimeLocation);
  if (isWebAlert) {
    try {
      notifyLocationChanged(window.top?.Mach, runtimeLocation);
    } catch (e) {
      // 跨域 iframe 无法访问 top 时保留当前 Mach 的已提交结果。
    }
  }
};

// 获取定位并存储，及广播通知
// 只更新实际定位
export async function setCurrentLocation(position?: { actualLatitude: string; actualLongitude: string; }) {
  try {
    const currLocation = normalizeStoredLocationForRuntime(); // 判断缓存中是否值，并区分即时状态和缓存恢复状态
    const { latitude: clatitude, longitude: clongitude } = currLocation || {};
    if (normalizeLocationCoordinates(clatitude, clongitude)) {
      // 已有定位且有值，且为正常定位值，才不更新
      return;
    }
    let { longitude, latitude } = getLocation();
    const { actualLatitude, actualLongitude } = position || (await getPosition());
    const {
      longitude: nearByLongitude, latitude: nearByLatitude, poiName, addressLocation, inServiceArea
    } = (await getNearbyAddress({ latitude, longitude })) || {}; // 使用选择的行为获取附近点，而不是实际定位
    setLocationInfo({
      actualLatitude,
      actualLongitude,
      longitude,
      latitude,
      poiName,
      name: poiName,
      addressLocation,
      inServiceArea,
      detail: {},
      locateStatus: LocatinStatus.actualLocation,
      type: LocatinType.actualPoi,
    });
  } catch (e) {
    console.log(e);
  }
}

const normalizeRegionChangeParams = (region, cityId) => {
  const normalizedRegion = typeof region === 'string' ? region.trim().toUpperCase() : '';
  const normalizedCityId = Number(cityId);
  if (!SUPPORT_REGION_SET.has(normalizedRegion)
    || !Number.isSafeInteger(normalizedCityId)
    || normalizedCityId <= 0) return null;
  return {
    region: normalizedRegion,
    cityId: normalizedCityId,
  };
};

const rollbackRegionChange = (region, cityId) => {
  try {
    setCityId(Number(cityId));
  } catch (e) {
    // 回滚 CityId 失败时仍继续尝试回滚 Region。
  }
  try {
    setRegion(region);
  } catch (e) {
    // 回滚失败由 Region 失败回执收口，避免异常阻断弹窗关闭。
  }
};

const commitRegionChange = (region, cityId) => {
  const previousRegion = getRegion();
  const previousCityId = getCityId();
  try {
    // Region listener 可能同步读取 CityId，因此必须先提交 CityId。
    setCityId(cityId);
    setRegion(region);
    const isCommitted = String(getCityId()) === String(cityId)
      && String(getRegion()).trim().toUpperCase() === region;
    if (isCommitted) {
      return {
        region: previousRegion,
        cityId: previousCityId,
      };
    }
  } catch (e) {
    // 提交失败后由下方统一回滚。
  }

  rollbackRegionChange(previousRegion, previousCityId);
  return null;
};

const captureStorageValue = (storage, key) => {
  try {
    return {
      captured: true,
      value: storage?.getItem(key) ?? null,
    };
  } catch (e) {
    return { captured: false, value: null };
  }
};

const restoreStorageValue = (storage, key, snapshot) => {
  if (!snapshot?.captured) return;
  try {
    if (snapshot.value === null) {
      storage?.removeItem(key);
    } else {
      storage?.setItem(key, snapshot.value);
    }
  } catch (e) {
    // Storage 不可用时不能继续恢复，但仍需完成 Compass 回滚和失败回执。
  }
};

// 来源用户选择存储的地址：选择字段 addressName/addressLocation 优先
export async function changeLocationBySelectAddress(params) {
  const revision = ++locationSelectionRevision;
  const {
    latitude, longitude, address, name, poiName: poiNameSe,
    addressName: addressNameSe, addressLocation: addressLocationSe,
  } = params || {};
  const coordinates = normalizeLocationCoordinates(latitude, longitude);
  if (!coordinates) return null;

  const preLocation = getLocation();
  const selectedName = [
    addressNameSe, name, poiNameSe, addressLocationSe, address,
    preLocation?.name, preLocation?.poiName
  ].find(isNonEmptyValue);
  const selectedAddressLocation = [
    addressLocationSe, address, addressNameSe, preLocation?.addressLocation, selectedName
  ].find(isNonEmptyValue);
  const newAddress = {
    ...preLocation,
    ...params,
    ...coordinates,
    poiName: selectedName,
    name: selectedName,
    addressLocation: selectedAddressLocation,
    inServiceArea: params?.inServiceArea ?? preLocation?.inServiceArea ?? true,
    detail: params,
    locateStatus: LocatinStatus.selectedUserAddress,
    type: LocatinType.userAddress,
  };

  if (revision !== locationSelectionRevision) return null;
  // 原生在位置状态广播前已保存用户地址槽，保证同步消费者读取到新地址。
  setStoredSelectedUserAddress(params);
  setLocationInfo(newAddress);
  emitHomeCoreComplete();
  return newAddress;
}

// 来源用户选择定位信息：name/address 是地图选点的权威字段，nearby 只在缺失时兜底
export async function changeLocationBySelectLocation(params) {
  const revision = ++locationSelectionRevision;
  const {
    latitude, longitude, address, name, poiName: poiNameSe,
    addressName: addressNameSe, addressLocation: addressLocationSe,
    region, cityId,
  } = params || {};
  const isChangeRegion = params?.is_change_region === true;
  const coordinates = normalizeLocationCoordinates(latitude, longitude);
  if (!coordinates) {
    if (isChangeRegion) emitRegionChangeResult(false);
    return null;
  }

  const regionChangeParams = isChangeRegion ? normalizeRegionChangeParams(region, cityId) : null;
  if (isChangeRegion && !regionChangeParams) {
    emitRegionChangeResult(false);
    return null;
  }

  const hasSelectedAddressInfo = [name, address, poiNameSe, addressNameSe, addressLocationSe]
    .some(isNonEmptyValue);
  const nearbyAddress = hasSelectedAddressInfo
    ? {}
    : (await getNearbyAddress(coordinates)) || {};
  if (revision !== locationSelectionRevision) return null;

  const preLocation = isChangeRegion
    ? { ...(DefaultLocation[regionChangeParams.region] || {}) }
    : getLocation();
  const regionLocationMetadata = isChangeRegion
    ? getRegionLocationMetadata(regionChangeParams.region, regionChangeParams.cityId)
    : {};
  const selectedName = [
    name, poiNameSe, addressNameSe, address, addressLocationSe,
    nearbyAddress?.poiName, nearbyAddress?.addressLocation,
    params?.city, preLocation?.name, preLocation?.poiName
  ].find(isNonEmptyValue);
  const selectedAddressLocation = [
    address, addressLocationSe, addressNameSe,
    nearbyAddress?.addressLocation, selectedName, preLocation?.addressLocation
  ].find(isNonEmptyValue);
  const newAddress = {
    ...preLocation,
    ...params,
    ...coordinates,
    ...regionLocationMetadata,
    poiName: selectedName,
    name: selectedName,
    addressLocation: selectedAddressLocation,
    inServiceArea: params?.inServiceArea ?? nearbyAddress?.inServiceArea ?? preLocation?.inServiceArea ?? true,
    detail: params,
    locateStatus: LocatinStatus.selectedLocation,
    type: LocatinType.selectedLocation,
  };

  const locationSnapshot = isChangeRegion
    ? captureStorageValue(localStorage, localKeys.LOCATION_INFO)
    : null;
  const selectedLocationSnapshot = isChangeRegion
    ? captureStorageValue(localStorage, localKeys.ADDRESS_LAST_SELSECTED)
    : null;
  const regionSnapshot = isChangeRegion
    ? commitRegionChange(regionChangeParams.region, regionChangeParams.cityId)
    : null;
  if (isChangeRegion && !regionSnapshot) {
    emitRegionChangeResult(false);
    return null;
  }

  try {
    storeLastSelected(newAddress);
    setLocationInfo(newAddress);
  } catch (e) {
    if (!isChangeRegion) throw e;
    restoreStorageValue(localStorage, localKeys.ADDRESS_LAST_SELSECTED, selectedLocationSnapshot);
    restoreStorageValue(localStorage, localKeys.LOCATION_INFO, locationSnapshot);
    if (regionSnapshot) {
      rollbackRegionChange(regionSnapshot.region, regionSnapshot.cityId);
    }
    if (isChangeRegion) emitRegionChangeResult(false);
    return null;
  }
  if (isChangeRegion) emitRegionChangeResult(true);
  emitHomeCoreComplete();
  return newAddress;
}
